// src/utils/geneticAnalysisUtils.ts
import { GeneStage } from '../../types/geneCodes/GeneStageTypes';
import { Staging } from '../../types/geneCodes/StagingTypes';

// R8: a fourth outcome, distinct from `null`. `null` is a SNP the array measured and could not
// call; UNGENOTYPED is a SNP the sample was never measured for at all — an absent result, not a
// result of "no risk".
export const UNGENOTYPED = 'ungenotyped';

export type SnpRiskStatus = 'good' | 'medium' | 'bad' | typeof UNGENOTYPED | null;

export interface ProcessedGeneticResult {
	snpId: string;
	riskStatus: SnpRiskStatus;
	allele1: string;
	allele2: string;
	riskAllele?: string;
	beneficialAllele?: string;
	numericRiskFactor: number; // Added to support legacy scoring
}

/**
 * Process genetic stage data and staging data to determine risk levels
 */
export const processGeneticResults = (
	geneticStages: GeneStage[],
	stagingData: Staging[]
): Map<string, ProcessedGeneticResult> => {
	const resultMap = new Map<string, ProcessedGeneticResult>();

	// Create a map of staging data for quick lookup
	const stagingMap = new Map<string, Staging>();
	stagingData.forEach(staging => {
		if (staging.rs) {
			stagingMap.set(staging.rs.toLowerCase(), staging);
		}
	});

	let processedCount = 0;
	let riskStatusCounts = { good: 0, medium: 0, bad: 0, null: 0 };

	// Process each genetic stage result
	geneticStages.forEach(geneStage => {
		const snpId = geneStage.rs_stage?.toLowerCase();
		if (!snpId) return;

		const staging = stagingMap.get(snpId);
		if (!staging) {
			// Skip if no staging data for this SNP - this is expected for many SNPs
			return;
		}

		const { riskStatus, numericRiskFactor } = calculateRiskStatusAndNumeric(
			geneStage.a1_stage,
			geneStage.a2_stage,
			staging.risk_allele,
			staging.bene_allele
		);

		resultMap.set(snpId, {
			snpId: snpId,
			riskStatus,
			numericRiskFactor,
			allele1: geneStage.a1_stage,
			allele2: geneStage.a2_stage,
			riskAllele: staging.risk_allele || undefined,
			beneficialAllele: staging.bene_allele || undefined
		});

		// Count risk status distribution
		if (riskStatus === 'good') riskStatusCounts.good++;
		else if (riskStatus === 'medium') riskStatusCounts.medium++;
		else if (riskStatus === 'bad') riskStatusCounts.bad++;
		else riskStatusCounts.null++;

		processedCount++;
	});

	// An empty stage list is "no sample loaded", the same reading the wheels take — nothing to
	// call ungenotyped against.
	if (geneticStages.length === 0) return resultMap;

	stagingData.forEach(staging => {
		const snpId = staging.rs?.toLowerCase();
		if (!snpId || resultMap.has(snpId)) return;

		resultMap.set(snpId, {
			snpId,
			riskStatus: UNGENOTYPED,
			numericRiskFactor: 0,
			allele1: '',
			allele2: '',
			riskAllele: staging.risk_allele || undefined,
			beneficialAllele: staging.bene_allele || undefined
		});
	});

	return resultMap;
};

/**
 * Calculate risk status and numeric risk factor based on allele comparison
 * This matches the old JavaScript system exactly
 */
export const calculateRiskStatusAndNumeric = (
	allele1: string,
	allele2: string,
	riskAllele: string | null,
	beneficialAllele: string | null
): { riskStatus: 'good' | 'medium' | 'bad' | null; numericRiskFactor: number } => {
	if (!riskAllele && !beneficialAllele) {
		return { riskStatus: null, numericRiskFactor: 0 }; // No risk/benefit information available
	}

	// Handle missing or undefined genotype data
	if (!allele1 || !allele2 ||
		allele1 === 'NOAMP' || allele2 === 'NOAMP' ||
		allele1 === 'UND' || allele2 === 'UND' ||
		!riskAllele) {
		return { riskStatus: null, numericRiskFactor: 0 }; // Grey in old system
	}

	// Normalize alleles to uppercase for comparison
	const a1 = allele1?.toUpperCase();
	const a2 = allele2?.toUpperCase();
	const risk = riskAllele?.toUpperCase();

	let riskCount = 0;

	// Count risk alleles - this matches the old JavaScript logic exactly
	if (risk) {
		if (a1 === risk) riskCount++;
		if (a2 === risk) riskCount++;
	}

	// Assign risk status and numeric factor exactly like the old system
	if (riskCount === 2) {
		// Two risk alleles - matches: node.data.A1 == node.data.rA && node.data.A2 == node.data.rA
		return { riskStatus: 'bad', numericRiskFactor: 3 }; // Red in old system, risk_factor = 3
	} else if (riskCount === 1) {
		// One risk allele - matches both heterozygous conditions in old system
		return { riskStatus: 'medium', numericRiskFactor: 2 }; // Orange in old system, risk_factor = 2
	} else {
		// No risk alleles - matches: node.data.A1 != node.data.rA && node.data.A2 != node.data.rA
		return { riskStatus: 'good', numericRiskFactor: 1 }; // Green in old system, risk_factor = 1
	}
};

/**
 * Calculate risk status based on allele comparison - legacy function for backwards compatibility
 */
export const calculateRiskStatus = (
	allele1: string,
	allele2: string,
	riskAllele: string | null,
	beneficialAllele: string | null
): 'good' | 'medium' | 'bad' | null => {
	const result = calculateRiskStatusAndNumeric(allele1, allele2, riskAllele, beneficialAllele);
	return result.riskStatus;
};

/**
 * Get numeric risk factor for a specific SNP
 */
export const getSnpNumericRiskFactor = (
	snp: Staging,
	geneticResults: Map<string, ProcessedGeneticResult>
): number => {
	if (!snp.rs) return 0;

	const result = geneticResults.get(snp.rs.toLowerCase());
	return result?.numericRiskFactor || 0;
};

/**
 * Filter SNPs based on genetic data availability
 *
 * R8, revised: an ungenotyped SNP carries a marker entry and survives this filter — unless the
 * caller passes `hideUngenotyped`, which only the patient-facing shell does.
 */
export const filterSnpsWithGeneticData = (
	snps: Staging[],
	geneticResults: Map<string, ProcessedGeneticResult>,
	hasGeneticData: boolean,
	// A patient must not see a node that can never carry data; an author must, hence the default.
	hideUngenotyped: boolean = false
): Staging[] => {
	if (!hasGeneticData) {
		// If no genetic data provided, return all SNPs
		return snps;
	}

	// Filter to only include SNPs that have genetic results
	return snps.filter(snp => {
		if (!snp.rs) return false;
		const result = geneticResults.get(snp.rs.toLowerCase());
		if (!result) return false;
		return !hideUngenotyped || result.riskStatus !== UNGENOTYPED;
	});
};

/**
 * Get risk status for a specific SNP
 */
export const getSnpRiskStatus = (
	snp: Staging,
	geneticResults: Map<string, ProcessedGeneticResult>
): SnpRiskStatus => {
	if (!snp.rs) return null;

	const result = geneticResults.get(snp.rs.toLowerCase());
	return result?.riskStatus || null;
};